import * as React from "react";

import { Box, Link, Typography } from "@material-ui/core";
import { useMutation } from "react-query";
import * as Redux from "react-redux";

import { MopButton } from "client/components";
import { OnboardingStatusContext } from "client/context";
import { Status, StatusMap } from "client/domain";
import { uiFieldMap as cashPayUiFieldMap } from "client/domain/FormBuilder/CashPayForm";
import { FormBuilder } from "client/domain/FormBuilder/FormBuilder";
import { StatusLabel } from "client/domain/OnboardingStatus/StatusLabel";
import { StatusStep } from "client/domain/OnboardingStatus/StatusStep";
import { useStyles } from "client/domain/OnboardingStatus/styles";
import {
  useMerchantModel,
  useOnboardingStatusContext,
  useRunOnce,
  useTranslation,
  useUiFieldMap,
} from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { UserService } from "client/services";
import { errorActions } from "client/store";

export const determineCurrentState: (
  params: Pick<
    OnboardingStatusContext,
    | "cashPayOnboardingStatus"
    | "cashPayIntegrationComplete"
    | "customizeCashPayProfileComplete"
  >
) => Status = ({
  cashPayOnboardingStatus,
  cashPayIntegrationComplete,
  customizeCashPayProfileComplete,
}) => {
  if (customizeCashPayProfileComplete) {
    return "Done";
  }

  if (
    cashPayOnboardingStatus === "READY_TO_INSTALL" &&
    cashPayIntegrationComplete
  ) {
    return "InProgress";
  }

  return "None";
};

export const CustomizeCashPayProfileStep: React.FC = () => {
  const {
    cashPayOnboardingStatus,
    cashPayIntegrationComplete,
    customizeCashPayProfileComplete,
    onCustomizeCashPayProfileComplete,
  } = useOnboardingStatusContext();
  const {
    merchant,
    formatMerchantForCashPayInfo,
    validateMerchant,
  } = useMerchantModel();
  const { orderedUiFieldMap } = useUiFieldMap(cashPayUiFieldMap);
  const t = useTranslation();
  const dispatch = Redux.useDispatch();
  const classes = useStyles();

  const currentState = determineCurrentState({
    cashPayIntegrationComplete,
    cashPayOnboardingStatus,
    customizeCashPayProfileComplete,
  });

  const statusMap: StatusMap = {
    None: {
      className: classes.light,
    },
    InProgress: {
      className: classes.light,
    },
    Done: {
      className: classes.dark,
    },
    Paused: {},
    Reviewing: {
      className: classes.dark,
    },
    RequiresAttention: {},
    Declined: {},
  };

  const { className } = statusMap[currentState];

  useRunOnce(() =>
    logAmplitudeEvent("CASH_STATUS_PAGE", "customize_cash_profile_card_loaded")
  );

  const { mutate: submitCashPayLogoUrl } = useMutation(
    UserService.updateCashPayProfileImageUrl,
    {
      onError: (err: Error) => {
        dispatch(errorActions.setError(err.message));
      },
      onSuccess: ({ success, error }) => {
        if (error) {
          dispatch(errorActions.setError(error));
        }

        if (success) {
          // Mark step as done
          onCustomizeCashPayProfileComplete();
          logAmplitudeEvent(
            "CASH_STATUS_PAGE",
            "customize_cash_profile_card_submitted"
          );
        }
      },
    }
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const isValid = validateMerchant(merchant.cashPay);
    if (isValid) {
      const data = formatMerchantForCashPayInfo();
      submitCashPayLogoUrl(data);
    }
  };

  const handleSkip = () => {
    logAmplitudeEvent(
      "CASH_STATUS_PAGE",
      "customize_cash_profile_card_skipped"
    );
    onCustomizeCashPayProfileComplete();
  };

  return (
    <StatusStep
      timeToFade={500}
      activeOnboardingStep="CustomizeCashPayProfile"
      stepStatus={currentState}
    >
      <Box className={`${className} ${classes.statusWrapper}`} mb={2} p={2}>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Typography variant="h6">
            <strong>{t("uiCustomizeCashPayProfile")}</strong>
          </Typography>
          <StatusLabel status={currentState} />
        </Box>
        {currentState === "InProgress" && (
          <Box
            className={classes.addCashPayStepValuePropFactsWrapper}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            mt={2}
          >
            <Typography variant="body2" className={classes.cashPayLogoSubtext}>
              Customize your merchant logo to build a stronger brand presence on
              Cash App. If no logo is uploaded, we &apos;ll display a standard
              category icon.
            </Typography>
            <Box mt={2} maxWidth={320}>
              <FormBuilder
                fieldSet={merchant.cashPay}
                onSubmit={handleSubmit}
                uiFieldMap={orderedUiFieldMap.cashPay}
                customSubmitComponent={
                  <Box display="flex" textAlign="center" alignItems="center">
                    <Box flex={1}>
                      <MopButton
                        mopVariant="primary"
                        type="submit"
                        fullWidth
                        className={classes.shortButton}
                        disabled={!merchant.cashPay.cashPayLogoUrl.value}
                      >
                        Submit
                      </MopButton>
                    </Box>
                    <Box flex={1}>
                      <Link
                        // https://mui.com/material-ui/react-link/#accessibility
                        component="button"
                        variant="body2"
                        onClick={handleSkip}
                        className={classes.skipStepLink}
                      >
                        <strong>Skip for now</strong>
                      </Link>
                    </Box>
                  </Box>
                }
              />
            </Box>
          </Box>
        )}
      </Box>
    </StatusStep>
  );
};
